import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Banner shown to cleaners who haven't verified their email
 * Can be dismissed but will reappear on next session
 */
const CleanerEmailVerificationBanner = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Only show for cleaners who haven't verified
  if (!user || profile?.role !== "cleaner" || (profile as any)?.email_verified || dismissed) {
    return null;
  }

  const handleSendVerification = async () => {
    if (!user?.email) return;
    
    setIsSending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: user.email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (error) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: t("emailSent"),
        description: t("checkInboxForVerification"),
      });
    }
    setIsSending(false);
  };

  return (
    <Alert className="mb-4 bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900">
      <Mail className="h-4 w-4 text-amber-600" />
      <AlertDescription className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-amber-800 dark:text-amber-200 text-sm">
          {t("verifyEmailForFeatures")}
        </span>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleSendVerification}
            disabled={isSending}
            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            {isSending ? t("sending") : t("verifyEmail")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
            className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default CleanerEmailVerificationBanner;
